import type { BillingInterval, BillingPlan } from 'definitions/billing-plans';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';

export type ClientUsageRow = {
  companyId: number;
  companyName: string;
  units: number;
  /** The client's own plan; null until the agency has chosen one. */
  plan: BillingPlan | null;
  interval: BillingInterval | null;
  status: string | null;
  hasLiveSubscription: boolean;
  /** How the client is billed. Only a card client has a plan to choose and pay for. */
  billingMode: 'card' | 'free' | 'invoice';
  /** The account the agency's fee includes. */
  included: boolean;
};

export type ClientUsage = { clients: ClientUsageRow[] };

/**
 * Agency-only: month-to-date website sessions for every client, from
 * `GET /api/billing/usage/clients`, with each client's own plan and its status.
 */
export const useClientUsage = () => {
  const apiClient = useClient();
  const { user } = useAuth();
  const isAgency = user?.company.type === 'Agency';

  const { data, isLoading, isError, refetch } = useQuery<ClientUsage>({
    queryKey: ['BILLING_CLIENT_USAGE'],
    enabled: !!user && isAgency,
    queryFn: () => apiClient.get('/billing/usage/clients'),
  });

  return { clientUsage: data, isLoading, isError, refetch };
};
